export type BirthDateDraft = { day: string; month: string; year: string }

export type BirthInputError = 'required' | 'format' | 'date' | 'time' | 'range'

// Only a valid result can be adapted to a page's DTO. Empty never means midnight.
export type BirthInputResult =
  | { status: 'valid'; value: string; error?: never }
  | { status: 'empty'; value?: never; error?: 'required' }
  | { status: 'incomplete' | 'invalid'; value?: never; error: BirthInputError }

export type BirthDateOptions = { required?: boolean; min?: string; max?: string }

export const EMPTY_BIRTH_DATE_DRAFT: BirthDateDraft = { day: '', month: '', year: '' }

// Decomposes a stored civil date into the three fields. Anything that is not a
// complete YYYY-MM-DD value starts as an empty draft instead of being guessed.
export function toBirthDateDraft(value?: string | null): BirthDateDraft {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim())
  return match ? { day: match[3], month: match[2], year: match[1] } : { ...EMPTY_BIRTH_DATE_DRAFT }
}

export function isEmptyBirthDateDraft(draft: BirthDateDraft) {
  return !draft.day.trim() && !draft.month.trim() && !draft.year.trim()
}

export function parseBirthDate(draft: BirthDateDraft, options: BirthDateOptions = {}): BirthInputResult {
  const parts = [draft.day.trim(), draft.month.trim(), draft.year.trim()]
  if (parts.every(part => !part)) {
    return options.required ? { status: 'empty', error: 'required' } : { status: 'empty' }
  }
  if (parts.some((part, index) => !/^\d*$/.test(part) || part.length > (index === 2 ? 4 : 2))) {
    return { status: 'invalid', error: 'format' }
  }
  if (parts.some(part => !part)) return { status: 'incomplete', error: 'required' }
  if (parts[2].length !== 4) return { status: 'incomplete', error: 'format' }

  const [day, month, year] = parts.map(Number)
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  if (year === 0 || month < 1 || month > 12 || day < 1 || day > days[month - 1]) {
    return { status: 'invalid', error: 'date' }
  }

  // Civil strings only: no Date construction, device timezone, or inferred century.
  const value = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
  if ((options.min && value < options.min) || (options.max && value > options.max)) {
    return { status: 'invalid', error: 'range' }
  }
  return { status: 'valid', value }
}

export function parseBirthTime(draft: string, required = false): BirthInputResult {
  const text = draft.trim()
  if (!text) return required ? { status: 'empty', error: 'required' } : { status: 'empty' }
  const match = /^(\d{1,2}):(\d{2})$/.exec(text) ?? /^(\d{2})(\d{2})$/.exec(text)
  if (!match) {
    const partial = /^\d{1,3}$/.test(text) || /^\d{1,2}:\d{0,1}$/.test(text)
    return { status: partial ? 'incomplete' : 'invalid', error: 'format' }
  }
  if (Number(match[1]) > 23 || Number(match[2]) > 59) return { status: 'invalid', error: 'time' }
  return { status: 'valid', value: `${match[1].padStart(2, '0')}:${match[2]}` }
}

// Outer spaces and presentation zeros are normalized when the edit ends; an invalid
// draft is kept as typed so that it can be corrected instead of silently replaced.
export function normalizeBirthDateDraft(draft: BirthDateDraft, options: BirthDateOptions = {}): BirthDateDraft {
  const result = parseBirthDate(draft, options)
  if (result.status !== 'valid') {
    return { day: draft.day.trim(), month: draft.month.trim(), year: draft.year.trim() }
  }
  const [year, month, day] = result.value.split('-')
  return { day, month, year }
}

// The only boundary the product puts on a birth date: it cannot be in the future.
// There is no lower boundary - an old date is accepted and computed by the ephemerides.
// Built from the UTC date so that it does not depend on the device time zone.
export function maxBirthDate(): string {
  return new Date().toISOString().slice(0, 10)
}

export function normalizeBirthTimeDraft(draft: string): string {
  const result = parseBirthTime(draft)
  return result.status === 'valid' ? result.value : draft.trim()
}

// GR-027: the API sends the civil birth date (YYYY-MM-DD) and time (HH:mm) already
// derived for the birth place. They are formatted on a UTC clock so that the device
// time zone never moves them; the stored UTC instant is never used for display.
function civilBirthAsUtcClock(date?: string | null, time?: string | null): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date ?? '').trim())
  if (!dateMatch) return null
  const timeMatch = /^(\d{2}):(\d{2})$/.exec((time ?? '').trim())
  const value = new Date(0)
  value.setUTCFullYear(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
  value.setUTCHours(timeMatch ? Number(timeMatch[1]) : 0, timeMatch ? Number(timeMatch[2]) : 0, 0, 0)
  return Number.isNaN(value.getTime()) ? null : value
}

export function formatCivilBirthDate(
  date: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = {},
): string | null {
  const value = civilBirthAsUtcClock(date)
  return value ? new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(value) : null
}

// Date and time together; without a civil time only the date is shown, never midnight.
export function formatCivilBirthDateTime(
  date: string | null | undefined,
  time: string | null | undefined,
  locale: string,
): string | null {
  const timeResult = parseBirthTime(time ?? '')
  const civilTime = timeResult.status === 'valid' ? timeResult.value : null
  const value = civilBirthAsUtcClock(date, civilTime)
  if (!value) return null
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    ...(civilTime ? { timeStyle: 'short' as const } : {}),
    timeZone: 'UTC',
  }).format(value)
}
